import * as AlertDialog from '@radix-ui/react-alert-dialog'
import { useState } from 'react'
import {
  CheckCircle2,
  Clock,
  Compass,
  LayoutDashboard,
  ListChecks,
  Search,
  Sparkles,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/shared/ui'
import { crmLive } from '@/core/models/club'
import type { ClubPermission } from '@/core/models/staff'
import { useAuthStore } from '@/core/auth/auth-store'
import { useCurrentClubStore } from '@/core/auth/current-club-store'
import { useCurrentRole } from '@/features/staff/hooks/use-current-role'
import { useStaffByUid } from '@/features/staff/hooks/use-staff'
import { useMarkWelcomeDialogSeen } from '@/features/staff/hooks/use-mark-welcome-dialog-seen'
import { useClub } from '@/features/settings/hooks/use-club'
import { useNotificationCenterStore } from './notification-center-store'

/**
 * Role-aware "first sign-in" welcome modal — the dashboard counterpart
 * of `PreviewWelcomeDialog`. Fires exactly once per user (persisted via
 * `staff/{uid}.welcomeDialogSeenAt`), explains what the operator should
 * do first based on their tier, then opens the matching first-steps
 * checklist after the dialog closes.
 *
 * Skipped for: preview sessions, platform admins (no real staff doc),
 * already-dismissed users. Renders nothing in those cases.
 */
export function WelcomeDialog() {
  const [dismissedFor, setDismissedFor] = useState<string | null>(null)
  const user = useAuthStore((s) => s.user)
  const clubId = useCurrentClubStore((s) => s.clubId)
  const uid = user?.uid ?? null
  const identity = clubId && uid ? `${clubId}:${uid}` : null
  const role = useCurrentRole()
  const clubQuery = useClub(clubId)
  const staffQuery = useStaffByUid(clubId, uid)
  const markSeen = useMarkWelcomeDialogSeen(clubId, uid)
  const closeCenter = useNotificationCenterStore((s) => s.closeCenter)
  const openCenter = useNotificationCenterStore((s) => s.openCenter)

  // Pick the single staff doc for this uid. `useStaffByUid` returns an
  // array because the underlying query filters on a field, not a doc
  // ID — but the rule that joined staff doc id == auth.uid guarantees
  // at most one match in practice.
  const staffDoc = staffQuery.data?.[0]

  // Guard list — render nothing unless every signal is positive. Each
  // guard handles a real case (preview visitor, admin, mid-load, already
  // dismissed); no defensive layering.
  if (!user || !clubId) return null
  if (dismissedFor === identity) return null
  if (role.staffRole == null) return null // preview / platform admin
  if (!staffDoc) return null // staff query still loading or no matching doc
  if (staffDoc.welcomeDialogSeenAt != null) return null

  const clubName = clubQuery.data?.name ?? null
  // Trial owners start with the inline, actionable setup panel on Hoy.
  if (role.tier === 'owner' && clubQuery.isPending) return null
  if (role.tier === 'owner' && clubQuery.data?.status === 'trial') return null
  const variant = pickVariant(role.tier)
  if (!variant) return null

  // Title carries the club name everywhere ("Bienvenido a {Club}") so the
  // operator immediately recognises this is their club. The per-role
  // tagline below the title sets context (sala de control / primer turno
  // / arrancar). Falls back to a club-agnostic greeting when the club
  // doc hasn't resolved yet.
  const title = clubName ? `Bienvenido a ${clubName}` : 'Bienvenido a tu club'

  return (
    <AlertDialog.Root
      open
      onOpenChange={(next) => {
        if (!next) {
          setDismissedFor(identity)
          markSeen()
          // Signup may have opened the checklist behind this modal. Reset
          // it now, then reopen after the dialog click and unmount complete.
          closeCenter()
          window.setTimeout(openCenter, 0)
        }
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="modal-scrim" />
        <AlertDialog.Content
          className="modal preview-welcome entry-welcome"
        >
          <div className="modal-head">
            <AlertDialog.Title asChild>
              <h2>
                <span aria-hidden="true">{variant.headEmoji}</span> {title}
              </h2>
            </AlertDialog.Title>
          </div>
          <div className="modal-body">
            <div className="preview-welcome-greeting">
              <h3>{variant.tagline}</h3>
              <AlertDialog.Description asChild>
                <p>{variant.lede}</p>
              </AlertDialog.Description>
            </div>

            <ul className="preview-welcome-cards">
              {variant.cards.filter(card => (!card.permission || role.can(card.permission)) && (!card.crm || crmLive(clubQuery.data))).map((card, i) => (
                <li
                  key={card.title}
                  className={
                    i === 0
                      ? 'preview-welcome-card preview-welcome-card--accent'
                      : 'preview-welcome-card'
                  }
                >
                  <div className="preview-welcome-card-icon" aria-hidden="true">
                    <card.Icon size={18} />
                  </div>
                  <div className="preview-welcome-card-body">
                    <h4>{card.title}</h4>
                    <p>{card.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="modal-foot">
            <AlertDialog.Action asChild>
              <Button variant="primary" size="sm" icon={Sparkles}>
                Empezar
              </Button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}

interface WelcomeCard {
  permission?: ClubPermission
  crm?: boolean
  Icon: LucideIcon
  title: string
  body: string
}

interface WelcomeVariant {
  headEmoji: string
  /** Per-role tagline shown under the title — sets context without
   *  repeating the club name. */
  tagline: string
  lede: string
  cards: WelcomeCard[]
}

function pickVariant(tier: 'owner' | 'manager' | 'staff' | null): WelcomeVariant | null {
  switch (tier) {
    case 'owner':
      return {
        headEmoji: '🎾',
        tagline: 'Tu club, listo para arrancar',
        lede:
          'Tu club ya está creado. Pulsa Empezar para ver tus primeros pasos y preparar el club a tu ritmo.',
        cards: [
          {
            Icon: ListChecks,
            title: 'Revisa pistas, horario y precios',
            body: 'Comprueba tu configuración real y confirma lo que esté listo. La guía conserva tus avances.',
          },
          {
            Icon: Compass,
            title: 'Lleva tus reservas al calendario',
            body: 'Abre el calendario y elige un hueco libre para registrar una reserva. Las reservas online se gestionan por separado.',
          },
          {
            Icon: Sparkles,
            title: 'Tu suscripción, siempre a mano',
            body: 'Consulta tu plan, fechas y condiciones en Ajustes → Suscripción. Invita al equipo cuando lo necesites.',
          },
        ],
      }
    case 'manager':
      return {
        headEmoji: '🛠️',
        tagline: 'Tu sala de control',
        lede:
          'Aquí gestionas el día a día del club: reservas, equipo y horario. Pulsa Empezar para ver tu guía.',
        cards: [
          {
            Icon: ListChecks,
            title: 'Empieza por Reservas',
            body:
              'Revisa y filtra las reservas del día. Las acciones disponibles dependen de tus permisos.',
          },
          {
            Icon: Users,
            title: 'Tu equipo, a tu lado',
            body:
              'Coordina el turno con tu equipo. El propietario gestiona las invitaciones; tus accesos dependen de los permisos de tu rol.',
          },
          {
            Icon: Clock,
            title: 'Horario claro = menos errores',
            body:
              'Consulta las horas de apertura. Si tu rol permite editar el horario, encontrarás los ajustes en la guía.',
          },
        ],
      }
    case 'staff':
      return {
        headEmoji: '🎾',
        tagline: 'Empieza tu turno',
        lede:
          'Tu guía muestra las secciones disponibles para tu rol. Empieza por conocer las tareas de tu turno.',
        cards: [
          {
            Icon: LayoutDashboard,
            permission: 'viewDashboard',
            title: 'Hoy: tu turno de un vistazo',
            body:
              'Consulta las cifras del día y lo pendiente. Si tienes permiso para crear reservas, elige un hueco libre del mapa.',
          },
          {
            Icon: Search,
            permission: 'viewCrm', crm: true,
            title: 'Encuentra a cualquier jugador',
            body:
              'Busca por teléfono, nombre o email en Clientes. Las reservas pasadas se enlazan a la ficha automáticamente.',
          },
          {
            Icon: CheckCircle2,
            permission: 'recordPayment',
            title: 'Cobros y estado de las reservas',
            body:
              'Abre una reserva y revisa su estado y el importe antes de confirmar. Las acciones disponibles dependen de tus permisos.',
          },
        ],
      }
    default:
      return null
  }
}
